import React, { useEffect, useState, useRef } from "react";
import styled from "styled-components";
import ReactSVG from "react-svg";
import CameraIcon from "assets/img/camera-top.svg";

const CameraButton = styled.button`
  position: fixed;
  background-color: #2f4051;
  top: ${({ top }) => top};
  left: 50%;
  transform: translateX(-50%);
  border: none;
  border-radius: 0 0 10px 10px;
  height: ${({ isMobile }) => (isMobile ? "38px" : "50px")};
  padding: ${({ isMobile }) => (isMobile ? "0 16px" : "0 20px")};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 1px 1px 10px rgba(0, 0, 0, 0.8);
  transition: all 0.3s ease;

  &:focus {
    outline: none;
  }
  
  .svg-camera {
    width: ${({ isMobile }) => (isMobile ? "30px" : "40px")};
    height: ${({ isMobile }) => (isMobile ? "30px" : "40px")};
  }
  
  h3 {
    padding-left: 10px;
    font-size: ${({ isMobile }) => (isMobile ? "12px" : "16px")};
    color: white;
    font-weight: bold;
  }
`;

const TopWrapper = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  transition: transform 0.3s ease;
  transform: translateY(${({ expanded, height }) => (expanded ? "0" : `-${height}`)});
  box-shadow: 1px 1px 10px rgba(0, 0, 0, 0.8);
  z-index: 2000;
`;

const CameraImg = styled.img`
  width: ${({ width }) => width};
  height: auto;
  min-width: 320px;
  min-height: 240px;
  object-fit: contain;
  border: 1px solid white;
  border-radius: 5px;
  cursor: pointer;
  margin: 0 15px;

  &:hover {
    border: 4px solid white;
    border-radius: 5px;
  }
`;

const CameraLabel = styled.div`
  margin-top: 8px;
  color: white;
  font-weight: bold;
  font-size: 14px;
`;

const ModalBox = styled.div`
  position: fixed;
  background-color: #2f4051;
  border-radius: 8px;
  box-shadow: 0 0 10px rgba(0,0,0,0.5);
  overflow: hidden;
  z-index: 2000;
`;

const ModalHeader = styled.div`
  height: 40px;
  background-color: #1c2b36;
  color: white;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 10px;
  cursor: move;

  button {
    background: none;
    border: none;
    color: white;
    font-size: 20px;
    cursor: pointer;
  }
`;

const ModalContent = styled.div`
  width: 100%;
  height: calc(100% - 40px);
  background-color: #2f4051;
`;

const ResizeHandle = styled.div`
  position: absolute;
  width: 16px;
  height: 16px;
  right: 0;
  bottom: 0;
  cursor: se-resize;
  background: white;
  border-radius: 3px;
`;

const CameraContainer = styled.div`
  flex: 0 0 auto;
  scroll-snap-align: start;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 320px;
  max-width: 100%;
`;

const CarouselWrapper = styled.div`
  width: 100%;
  height: ${({ height }) => height};
  background-color: #2f4051;
  overflow-x: hidden;
  overflow-y: hidden;
  display: flex;
  scroll-snap-type: x mandatory;
  scroll-behavior: smooth;
  cursor: grab;
  -webkit-overflow-scrolling: touch;
  padding: 20px 0;

  /* Edge fade gradients */
  &::before,
  &::after {
    content: "";
    position: absolute;
    top: 0;
    width: 40px;
    height: 100%;
    z-index: 1;
    pointer-events: none;
  }

  &::before {
    left: 0;
    background: linear-gradient(to right, #2f4051, transparent);
  }

  &::after {
    right: 0;
    background: linear-gradient(to left, #2f4051, transparent);
  }
`;

export const CameraWrapper = ({
  expanded,
  setExpanded,
  expansionHeight,
  cams,
  isMobile,
}) => {
  const [openModals, setOpenModals] = useState({});

  const openModal = (cam) => {
    setOpenModals(prev => ({
      ...prev,
      [cam.id]: cam
    }));
  };

  const closeModal = (camId) => {
    setOpenModals(prev => {
      const updated = { ...prev };
      delete updated[camId];
      return updated;
    });
  };

  const carouselRef = useRef(null);

  useEffect(() => {
    if (!expanded || !carouselRef.current) return;

    const el = carouselRef.current;
    let isDown = false;
    let startX, scrollLeft;
    let isTouching = false;

    const mouseDown = (e) => {
      isDown = true;
      startX = e.pageX - el.offsetLeft;
      scrollLeft = el.scrollLeft;
      el.style.cursor = "grabbing";
    };

    const mouseLeave = () => {
      isDown = false;
      el.style.cursor = "grab";
    };

    const mouseUp = () => {
      isDown = false;
      el.style.cursor = "grab";
    };

    const mouseMove = (e) => {
      if (!isDown) return;
      e.preventDefault();
      const x = e.pageX - el.offsetLeft;
      const walk = (x - startX) * 1.5; // scroll speed
      el.scrollLeft = scrollLeft - walk;
    };

    const handleTouchStart = (e) => {
      isTouching = true;
      startX = e.touches[0].pageX - el.offsetLeft;
      scrollLeft = el.scrollLeft;
    };

    const handleTouchMove = (e) => {
      if (!isTouching) return;
      const x = e.touches[0].pageX - el.offsetLeft;
      const walk = (x - startX) * 1.5;
      el.scrollLeft = scrollLeft - walk;
    };

    const handleTouchEnd = () => {
      isTouching = false;
    };

    el.addEventListener("mousedown", mouseDown);
    el.addEventListener("mouseleave", mouseLeave);
    el.addEventListener("mouseup", mouseUp);
    el.addEventListener("mousemove", mouseMove);
    el.addEventListener("touchstart", handleTouchStart);
    el.addEventListener("touchmove", handleTouchMove);
    el.addEventListener("touchend", handleTouchEnd);

    return () => {
      el.removeEventListener("mousedown", mouseDown);
      el.removeEventListener("mouseleave", mouseLeave);
      el.removeEventListener("mouseup", mouseUp);
      el.removeEventListener("mousemove", mouseMove);
      el.removeEventListener("touchstart", handleTouchStart);
      el.removeEventListener("touchmove", handleTouchMove);
      el.removeEventListener("touchend", handleTouchEnd);
    };
  }, [expanded]);

  return (
    <TopWrapper
      expanded={expanded}
      height={expansionHeight}
    >
      <CameraButton
        onClick={() => setExpanded(!expanded)}
        top={expansionHeight}
        isMobile={isMobile}
      >
        <ReactSVG
          src={CameraIcon}
          svgClassName="svg-camera"
        />
        <h3>MONITORAMENTO</h3>
      </CameraButton>
      {expanded && (
        <CarouselWrapper ref={carouselRef} height={expansionHeight}>
          {cams.map(cam => (
            <CameraStream
              key={cam.id}
              cam={cam}
              width="320px"
              onClick={() => openModal(cam)}
              isMobile={isMobile}
            />
          ))}
        </CarouselWrapper>
      )}
      {Object.entries(openModals).map(([id, cam]) => (
        <DraggableResizableModal
          key={id}
          cam={cam}
          onClose={() => closeModal(id)}
        />
      ))}
    </TopWrapper>
  );
};

const CameraStream = ({
  cam,
  width = "320px",
  onClick,
  showArrows,
  isMobile,
}) => {
  const handleClick = () => {
    if (isMobile) {
      window.open(`/camera/video/${cam.id}`, "_blank");
    } else {
      onClick();
    }
  };

  return (
    <CameraContainer
      showArrows={showArrows}
    >
      <CameraImg
        src={`/camera/video/${cam.id}`}
        alt="Camera Stream"
        width={width}
        onClick={handleClick}
      />
      <CameraLabel>{cam.name || `Camera ${cam.id}`}</CameraLabel>
    </CameraContainer>
  )
};

const DraggableResizableModal = ({ cam, onClose }) => {
  const modalRef = useRef(null);
  const [position, setPosition] = useState({ x: 100, y: 100 });
  const [size, setSize] = useState({ width: 320, height: 240 });
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const offset = useRef({ x: 0, y: 0 });

  const startDrag = (e) => {
    setIsDragging(true);
    offset.current = {
      x: e.clientX - position.x,
      y: e.clientY - position.y,
    };
  };

  const onDrag = (e) => {
    if (isDragging) {
      setPosition({
        x: e.clientX - offset.current.x,
        y: e.clientY - offset.current.y,
      });
    }
  };

  const stopDrag = () => setIsDragging(false);

  const startResize = (e) => {
    setIsResizing(true);
    e.stopPropagation();
  };

  const onResize = (e) => {
    if (isResizing) {
      setSize({
        width: Math.max(320, e.clientX - position.x),
        height: Math.max(240, e.clientY - position.y),
      });
    }
  };

  const stopResize = () => setIsResizing(false);

  useEffect(() => {
    document.addEventListener("mousemove", onDrag);
    document.addEventListener("mouseup", stopDrag);
    document.addEventListener("mousemove", onResize);
    document.addEventListener("mouseup", stopResize);
    return () => {
      document.removeEventListener("mousemove", onDrag);
      document.removeEventListener("mouseup", stopDrag);
      document.removeEventListener("mousemove", onResize);
      document.removeEventListener("mouseup", stopResize);
    };
  });

  return (
    <ModalBox
      ref={modalRef}
      style={{
        left: position.x,
        top: position.y,
        width: size.width,
        height: size.height,
      }}
    >
      <ModalHeader onMouseDown={startDrag}>
        <span>{cam.name || `Camera ${cam.id}`}</span>
        <button onClick={onClose} type="submit">×</button>
      </ModalHeader>
      <ModalContent>
        <img
          src={`/camera/video/${cam.id}`}
          alt={`Camera ${cam.id}`}
          style={{ width: "100%", height: "100%", objectFit: "contain" }}
        />
      </ModalContent>
      <ResizeHandle onMouseDown={startResize} />
    </ModalBox>
  );
};
